import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, ShieldCheck, CheckCircle2, XCircle, ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';
import useHomePath from '@/hooks/useHomePath';

const SECTION_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
const SECTION_COLOR = { probound: '#2F5E96', proepoxy: '#7A0B7A', prodecor: '#D4A24B' };

export default function AdminApprovals() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const [pending, setPending] = useState([]);
  const [allCerts, setAllCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // `${user_id}-${section}-${action}`
  const [expanded, setExpanded] = useState({}); // key: `${user_id}-${section}`
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    setLoading(true);
    const [p, all] = await Promise.all([
      fetch(`${API_URL}/api/admin/certifications/pending`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/admin/certifications`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
    ]);
    setPending(p);
    setAllCerts(all);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const action = async (type, cert) => {
    const k = `${cert.user_id}-${cert.section}-${type}`;
    setBusy(k);
    try {
      const res = await fetch(`${API_URL}/api/admin/certifications/${type}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: cert.user_id, section: cert.section }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || `${type} failed`);
      toast.success(`Certification ${type === 'approve' ? 'approved' : 'revoked'} for ${cert.installer_name} (${SECTION_LABEL[cert.section]})`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const rows = showAll ? allCerts : pending;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-4">
          <button onClick={() => navigate(-1)} data-testid="admin-approvals-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl md:text-2xl font-bold flex items-center gap-2 truncate">
              <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" /> Approved Installers
            </h1>
            <p className="hidden sm:block text-white/70 text-sm">
              {pending.length} installer{pending.length === 1 ? '' : 's'} awaiting review · {allCerts.filter(c => c.status === 'approved').length} approved · {allCerts.filter(c => c.status === 'at_risk').length} at risk
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              data-testid="toggle-show-all"
              onClick={() => setShowAll(s => !s)}
              size="sm"
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-2 sm:px-3"
            >
              <span className="sm:hidden">{showAll ? 'Pending' : 'All'}</span>
              <span className="hidden sm:inline">{showAll ? 'Show Pending Only' : 'Show All Certifications'}</span>
            </Button>
            <Button onClick={() => navigate(homePath)} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-2 sm:px-3" aria-label="Home">
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Home</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-3">
        {loading ? (
          <Card className="p-10 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</Card>
        ) : rows.length === 0 ? (
          <Card className="p-10 text-center text-gray-500">
            {showAll ? 'No certifications yet — installers will appear here once they hit eligibility.' : 'No pending approvals right now.'}
          </Card>
        ) : (
          rows.map(cert => {
            const k = `${cert.user_id}-${cert.section}`;
            const ev = cert.evidence || {};
            const proj = ev.latest_completed_project;
            return (
              <Card key={k} data-testid={`admin-cert-row-${cert.user_id}-${cert.section}`} className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
                      style={{ background: SECTION_COLOR[cert.section] }}
                    >
                      {SECTION_LABEL[cert.section]}
                    </span>
                    <div>
                      <div className="font-bold text-gray-900">{cert.installer_name || cert.installer_email || cert.user_id}</div>
                      <div className="text-xs text-gray-500">{cert.company_name || cert.installer_email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full
                      ${cert.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        cert.status === 'eligible_pending' ? 'bg-blue-100 text-blue-700' :
                        cert.status === 'at_risk' ? 'bg-amber-100 text-amber-700' :
                        cert.status === 'suspended' ? 'bg-rose-100 text-rose-700' :
                        'bg-gray-100 text-gray-600'}`}>
                      {cert.status.replace('_', ' ')}
                    </span>
                    <button
                      onClick={() => setExpanded(s => ({ ...s, [k]: !s[k] }))}
                      className="p-1 text-gray-500 hover:text-gray-900"
                    >
                      {expanded[k] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {expanded[k] && (
                  <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      {ev.completed_projects > 0 ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-gray-300" />}
                      Completed projects (30d): <strong>{ev.completed_projects}</strong>
                    </div>
                    <div className="flex items-center gap-2">
                      {proj?.all_categories_covered ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-gray-300" />}
                      All photo categories covered
                    </div>
                    <div className="flex items-center gap-2">
                      {ev.videos_all_watched ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-gray-300" />}
                      Training videos: <strong>{ev.videos_watched}/{ev.videos_total}</strong>
                    </div>
                    <div className="flex items-center gap-2">
                      {ev.orders_in_window > 0 ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-gray-300" />}
                      App orders (30d): <strong>{ev.orders_in_window}</strong>
                    </div>
                    {proj && (
                      <div className="md:col-span-2 text-xs text-gray-500">
                        Latest project: <strong>{proj.reference}</strong> — {proj.customer_name} ({Object.entries(proj.photos_by_category || {}).map(([c, n]) => `${c}: ${n}`).join(' · ')})
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2 flex-wrap">
                  {(cert.status === 'eligible_pending' || cert.status === 'suspended' || cert.status === 'not_eligible') && (
                    <Button
                      data-testid={`approve-${cert.user_id}-${cert.section}`}
                      onClick={() => action('approve', cert)}
                      size="sm"
                      disabled={busy === `${cert.user_id}-${cert.section}-approve`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  )}
                  {(cert.status === 'approved' || cert.status === 'at_risk') && (
                    <Button
                      data-testid={`revoke-${cert.user_id}-${cert.section}`}
                      onClick={() => action('revoke', cert)}
                      size="sm"
                      variant="outline"
                      disabled={busy === `${cert.user_id}-${cert.section}-revoke`}
                      className="text-rose-600 hover:bg-rose-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Revoke
                    </Button>
                  )}
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
